import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useTrip } from '../lib/db';
import { myUnit } from '../lib/split';
import { Icon } from '../components/Icons';
import { Loading, Note, Page, useToast } from '../components/ui';
import Expenses from './Expenses';
import Balances from './Balances';
import Families from './Families';
import AddExpense from './AddExpense';
import Settle from './Settle';
import ExportSheet from './ExportSheet';

export type TripTab = 'expenses' | 'balances' | 'families';

export interface SettleTarget { fromUnitId: string; toUnitId: string; amountMinor: number }

export default function Trip({ tab }: { tab: TripTab }) {
  const { groupId = '' } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const toast = useToast();
  const { data, loading, error } = useTrip(groupId);

  const [adding, setAdding] = useState(false);
  const [settling, setSettling] = useState<SettleTarget | null>(null);
  const [exporting, setExporting] = useState(false);

  if (loading && !data) return <Loading />;

  if (error || !data) {
    return (
      <Page title="Trip" left={<BackButton onClick={() => nav('/')} />}>
        <Note>
          {error ?? "This trip no longer exists, or you're not a member of it."}
        </Note>
        <button className="btn ghost" style={{ marginTop: 14 }} onClick={() => nav('/')}>
          Back to my trips
        </button>
      </Page>
    );
  }

  const { group, units } = data;
  const mine = myUnit(units, user?.uid);

  const tabs: { id: TripTab; label: string; to: string }[] = [
    { id: 'expenses', label: 'Expenses', to: `/t/${groupId}` },
    { id: 'balances', label: 'Balances', to: `/t/${groupId}/balances` },
    { id: 'families', label: 'Families', to: `/t/${groupId}/families` }
  ];

  const showFab = tab !== 'families' && Boolean(mine);

  return (
    <Page
      title={group.name}
      subtitle={[group.when, `${units.length} ${units.length === 1 ? 'family' : 'families'}`, group.currency]
        .filter(Boolean).join(' · ')}
      left={<BackButton onClick={() => nav('/')} />}
      right={
        <button className="iconbtn" aria-label="Export this trip" onClick={() => setExporting(true)}>
          <Icon name="export" />
        </button>
      }
      noFab={!showFab}
      fab={showFab ? (
        <button className="fab" onClick={() => setAdding(true)}>
          <Icon name="plus" /><span>Add expense</span>
        </button>
      ) : null}
    >
      <div className="sticky-seg">
        <div className="seg" role="tablist">
          {tabs.map((t) => (
            <Link key={t.id} to={t.to} role="tab"
              aria-selected={tab === t.id} replace>
              {t.label}
            </Link>
          ))}
        </div>
      </div>

      {!mine && tab !== 'families' && (
        <div className="banner" style={{ marginBottom: 12 }}>
          <Icon name="info" />
          <div>
            You haven't said which family you are yet, so balances can't include you.
            <Link to={`/t/${groupId}/families`} replace style={{ fontWeight: 700 }}> Pick your family →</Link>
          </div>
        </div>
      )}

      {tab === 'expenses' && <Expenses data={data} uid={user!.uid} />}
      {tab === 'balances' && <Balances data={data} uid={user!.uid} onSettle={setSettling} />}
      {tab === 'families' && <Families data={data} uid={user!.uid} />}

      {adding && (
        <AddExpense
          data={data}
          uid={user!.uid}
          onClose={() => setAdding(false)}
          onSaved={(label) => { setAdding(false); toast(label); }}
        />
      )}

      {settling && (
        <Settle
          data={data}
          uid={user!.uid}
          target={settling}
          onClose={() => setSettling(null)}
          onSaved={(label) => { setSettling(null); toast(label); }}
        />
      )}

      {exporting && <ExportSheet data={data} onClose={() => setExporting(false)} />}
    </Page>
  );
}

export const BackButton = ({ onClick }: { onClick: () => void }) => (
  <button className="iconbtn" onClick={onClick} aria-label="Back">
    <Icon name="back" />
  </button>
);
